import { useState, useEffect, useCallback } from 'react';
import {
  fetchClaimMdClaimNotes, fetchClaimMdClaimModifications, fetchClaimMdClaimResponses,
} from '../services/api';
import './ClaimMdActivityPanel.css';

const toArray = (v) => (v == null ? [] : Array.isArray(v) ? v : [v]);

function fmtDateTime(d) {
  if (!d) return '';
  const dt = new Date(d);
  if (isNaN(dt)) return String(d);
  return dt.toLocaleString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' });
}

// Claim.MD status letters → readable bucket (mirrors the backend's mapClaimMdStatus).
function statusLabel(code) {
  if (!code) return null;
  const c = String(code).toUpperCase();
  if (c.startsWith('A')) return 'Accepted';
  if (c.startsWith('R') || c.startsWith('E')) return 'Rejected';
  if (c.startsWith('P')) return 'Pending';
  return code;
}

export default function ClaimMdActivityPanel({ claim }) {
  const claimId   = claim.claim_id ?? claim.id;
  const claimmdId = claim.claimmd_id;

  const [notes, setNotes]                 = useState([]);
  const [modifications, setModifications] = useState([]);
  const [loading, setLoading]             = useState(true);

  const [liveStatus, setLiveStatus]       = useState(null); // [{status, messages}]
  const [checking, setChecking]           = useState(false);
  const [checkErr, setCheckErr]           = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [n, m] = await Promise.all([
        fetchClaimMdClaimNotes(claimId).catch(() => ({ notes: [] })),
        fetchClaimMdClaimModifications(claimId).catch(() => ({ modifications: [] })),
      ]);
      setNotes(n.notes || []);
      setModifications(m.modifications || []);
    } finally {
      setLoading(false);
    }
  }, [claimId]);

  useEffect(() => { load(); }, [load]);

  const handleCheckStatus = async () => {
    if (!claimmdId) return;
    setChecking(true);
    setCheckErr(null);
    try {
      const res = await fetchClaimMdClaimResponses(claimmdId);
      setLiveStatus(toArray(res.responses));
    } catch (err) {
      setCheckErr(err?.response?.data?.message || err?.response?.data?.error || 'Could not fetch status from Claim.MD.');
    } finally {
      setChecking(false);
    }
  };

  return (
    <div className="cd-card cd-full-card">
      <div className="cd-card-header cma-header">
        <h3 className="cd-card-title">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
          </svg>
          Clearinghouse Activity
        </h3>
        <button className="cma-check-btn" onClick={handleCheckStatus} disabled={checking || !claimmdId}>
          {checking ? 'Checking…' : 'Check Status'}
        </button>
      </div>

      <div className="cd-card-body">
        {/* Current / live status */}
        <div className="cma-status-row">
          <span className="cma-status-current">
            Current status:&nbsp;
            <strong>{statusLabel(claim.claimmd_status) || claim.claim_status || '—'}</strong>
            {claim.claimmd_status && <span className="cma-status-code">({claim.claimmd_status})</span>}
          </span>
          {claimmdId && <span className="cma-claimmd-id">Claim.MD ID: {claimmdId}</span>}
        </div>

        {checkErr && <div className="cma-error">{checkErr}</div>}
        {liveStatus && (
          liveStatus.length === 0 ? (
            <div className="cma-live cma-live-empty">No new status responses from Claim.MD.</div>
          ) : (
            <div className="cma-live">
              {liveStatus.map((r, i) => (
                <div key={i} className="cma-live-item">
                  <span className={`cma-pill cma-pill-${(statusLabel(r.status) || '').toLowerCase()}`}>
                    {statusLabel(r.status) || r.status || 'Status'}
                  </span>
                  <span className="cma-live-msg">
                    {toArray(r.messages).map((m) => m.message).filter(Boolean).join('; ') || 'No message provided.'}
                  </span>
                </div>
              ))}
            </div>
          )
        )}

        {loading ? (
          <div className="cma-loading">Loading clearinghouse activity…</div>
        ) : (
          <div className="cma-cols">
            {/* Notes */}
            <div className="cma-col">
              <div className="cma-col-title">Notes <span className="cma-count">{notes.length}</span></div>
              {notes.length === 0 ? (
                <div className="cma-empty">No clearinghouse notes.</div>
              ) : (
                <ul className="cma-list">
                  {notes.map((n) => (
                    <li key={n.noteid} className="cma-note">
                      <div className="cma-note-text">{n.note}</div>
                      <div className="cma-meta">
                        {n.username ? `${n.username} · ` : ''}{fmtDateTime(n.note_time)}
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </div>

            {/* Modifications */}
            <div className="cma-col">
              <div className="cma-col-title">Field Changes <span className="cma-count">{modifications.length}</span></div>
              {modifications.length === 0 ? (
                <div className="cma-empty">No field changes recorded.</div>
              ) : (
                <ul className="cma-list">
                  {modifications.map((m) => (
                    <li key={m.modid} className="cma-mod">
                      <div className="cma-mod-field">{m.field || 'Field'}</div>
                      <div className="cma-mod-change">
                        <span className="cma-was">{m.was_value || '—'}</span>
                        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><line x1="5" y1="12" x2="19" y2="12" /><polyline points="12 5 19 12 12 19" /></svg>
                        <span className="cma-now">{m.new_value || '—'}</span>
                      </div>
                      <div className="cma-meta">
                        {m.username ? `${m.username} · ` : ''}{fmtDateTime(m.mod_time)}
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
